import { Modal } from "@components/Modal";
import { FullPicker } from "@components/ImagePicker/FullPicker";
import { FirebaseFreshImage } from "@components/FirebaseCachedImage/FirebaseFreshImage";
import { DeleteIcon } from "@components/Icons/Delete";
import { FC, useContext, useState } from "react";
import { useTranslation } from "@helpers/useTranslation";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { ref, uploadBytes, deleteObject } from "firebase/storage";

type Props = {
  show: boolean;
  onClose: () => void;
  challengeId: string;
  introPhotoUri?: string;
  onPhotoChange: (introPhotoUri: string | undefined) => void;
};

export const IntroPhotoModal: FC<Props> = ({ show, onClose, challengeId, introPhotoUri, onPhotoChange }) => {
  const { t } = useTranslation("admin");
  const { storage } = useContext(FirebaseContext);
  const eventId = useEventId();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const basePath = `events/${eventId}/challenges/${challengeId}/intro`;

  const handleNewImage = async (file: File) => {
    setUploading(true);
    try {
      const storageRef = ref(storage, basePath);
      const metadata = {
        customMetadata: {
          isIntroPhoto: "true",
          challengeId,
          eventId,
        },
      };
      await uploadBytes(storageRef, file, metadata);
      onPhotoChange(`${basePath}_light`);
    } catch (error) {
      console.error("Error uploading intro photo:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!introPhotoUri) return;
    setDeleting(true);
    try {
      const lightRef = ref(storage, `${basePath}_light`);
      const heavyRef = ref(storage, `${basePath}_heavy`);
      const thumbRef = ref(storage, `${basePath}_thumb`);
      await Promise.allSettled([deleteObject(lightRef), deleteObject(heavyRef), deleteObject(thumbRef)]);
      onPhotoChange(undefined);
    } catch (error) {
      console.error("Error deleting intro photo:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal className="z-50" show={show} onCancel={onClose} size="lg">
      <h3 className="mb-2 text-lg font-bold">{t("introPhoto")}</h3>
      <p className="mb-4 text-sm opacity-70">{t("introPhotoDescription")}</p>

      {introPhotoUri ? (
        <div className="flex flex-col items-center">
          <div className="mb-4 max-w-md">
            <FirebaseFreshImage uri={introPhotoUri} alt={t("introPhoto")} />
          </div>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`btn-error btn ${deleting ? "loading" : ""}`}
            >
              <DeleteIcon className="mr-2 h-4 w-4" />
              {t("delete")}
            </button>
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <FullPicker
            uploadLabel={t("uploadIntroPhoto")}
            reuploadLabel={t("reuploadIntroPhoto")}
            onNewImage={handleNewImage}
            allowUpload={true}
          />
          {uploading && (
            <div className="mt-4 flex justify-center">
              <div className="loading-spinner loading-lg loading"></div>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
